import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { useBoolean } from 'react-use'

import { useUpdateReferralMutation } from '@/resolvers/user/mutation'
import useAuth from '@/hooks/useAuth'
import { useReqError } from '@/hooks/useReqError'
import { useDebounceInput, usePrompt } from '@/shared/makyo-hooks'
import { sleep } from '@/shared/utils'

/**
 * useMyReferral is a custom hook to manage the state and interactions
 * of referrals in your React component.
 *
 * @returns {Object} - Contains the loading state, the current referral value, the validation status of the referral,
 *                     functions to save referral, handle input change, copy referral link, and translation function.
 * @property {boolean} isLoading - The loading state used when saving the referral.
 * @property {string} val - The current referral value.
 * @property {boolean} isValid - The validation status of the referral. True if the referral is valid, false otherwise.
 * @property {function} onSave - The function to save the current referral value. It sets the loading state to true
 *                               before starting the saving process and to false when it's finished.
 * @property {function} onInputChange - The function to handle user input change in the form field. It also removes
 *                                      any non-alphanumeric characters from the user input.
 * @property {function} handleCopyUri - The function to copy the referral link. It uses the Clipboard API to write the
 *                                      referral link to the clipboard.
 * @property {boolean} checkIsLoading - The loading state used when checking the validity of the referral.
 * @property {string} shareUri - The encoded referral link.
 * @property {function} t - The function from the 'react-i18next' package for translating text.
 *
 * @example
 *
 * const { isLoading, val, isValid, onSave, onInputChange, handleCopyUri, checkIsLoading, shareUri, t } = useMyReferral();
 *
 */
const useMyReferral = () => {
  const { t } = useTranslation()
  const { userData, updateReferral } = useAuth()
  const [isLoading, setIsLoading] = useBoolean(false)
  const [checkIsLoading, setCheckIsLoading] = useBoolean(false)
  const checkIsValid = (value: string) => {
    return value.length > 6
  }

  const [debounceVal, , , val, setVal] = useDebounceInput(userData?.referral || '')

  const [isValid, setIsValid] = useBoolean(checkIsValid(val))
  const [dirty, setDirty] = useState<boolean>(false)
  const shareUri = encodeURIComponent(`https://makyo.me/ref/${val}`)
  const printAPIError = useReqError()

  const { mutate: updateReferralMutation } = useUpdateReferralMutation()
  usePrompt(t('formDirtyNote'), dirty)

  // This effect runs whenever the `debounceVal`, `setIsValid`, `setDirty`, or `setCheckIsLoading` changes.
  useEffect(() => {
    // Check the validity of the debounced value.
    const isDebValid = checkIsValid(debounceVal)
    // Update `isValid` state with the result of the check.
    setIsValid(isDebValid)

    // Only perform the following actions if the debounced value is valid.
    if (isDebValid) {
      setCheckIsLoading(true)
      // Use the `sleep` function to simulate loading time.
      sleep(400)
        .then(() => {
          // Perform operations here.
        })
        // Set the loading state to false after the operations are finished.
        .finally(() => setCheckIsLoading(false))
    }
  }, [debounceVal, setIsValid, setDirty, setCheckIsLoading])

  // `onInputChange` function handles the action of user input changing in the form field.
  // It also removes any non-alphanumeric characters from the user input.
  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVal = e.currentTarget.value.replaceAll(/\W|_/gi, '')
    setVal(newVal)
  }

  // `onSave` function handles the action of saving the user input.
  // It sets the `isLoading` state to true before starting the saving process and to false when it's finished.
  const onSave = () => {
    setIsLoading(true)
    setDirty(false)

    updateReferralMutation(
      { value: val },
      {
        onSuccess() {
          updateReferral(val)
          toast(t('Referral code saved!'))
        },
        onError(e) {
          printAPIError(e)
        },
        onSettled() {
          setIsLoading(false)
        },
      }
    )
  }

  // `handleCopyUri` function handles the action of copying the referral link.
  // It uses the Clipboard API to write the referral link to the clipboard.
  const handleCopyUri = () => {
    navigator.clipboard.writeText(`https://makyo.me/ref/${val}`)
    toast(t('Copied to clipboard!'))
  }

  return {
    isLoading,
    val,
    isValid,
    onSave,
    onInputChange,
    handleCopyUri,
    checkIsLoading,
    shareUri,
    t,
  }
}

export default useMyReferral
